import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import styles from './ManageEnterprises.module.scss'
import billingOpsStyles from '@/pages/app/BillingOps/styles.module.scss'

import { isApiError } from '@/api'
import { commitBillingOp, previewBillingOp } from '@/api/billingOps'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { enterpriseDetailsQueryKey } from '@/hooks/useEnterpriseDetails'
import ConvertToSalesOwnedForm from '@/pages/app/BillingOps/components/ConvertToSalesOwnedForm'
import PreviewPanel from '@/pages/app/BillingOps/components/PreviewPanel'
import { BillingOpsPreviewView, ConvertToSalesOwnedInput, OPERATION_KINDS } from '@/pages/app/BillingOps/types'

const PILOT_BANNER =
  'Converts this pilot to a signed contract. Access continues with no end date, and the seats below are recorded. ' +
  'Run this once the contract is signed.'

const FORM_ID = 'manage-enterprise-convert-to-sales-owned'

interface ConvertDialogProps {
  enterpriseId: string
  costCenterId: string
  close: () => void
  // A sales-owned pilot has nothing in Stripe; converting only changes our database.
  isSalesOwnedPilot?: boolean
}

const ConvertToSalesOwnedDialog = ({
  enterpriseId,
  costCenterId,
  close,
  isSalesOwnedPilot = false,
}: ConvertDialogProps) => {
  const queryClient = useQueryClient()
  const [preview, setPreview] = useState<BillingOpsPreviewView | null>(null)
  const [previewedInput, setPreviewedInput] = useState<ConvertToSalesOwnedInput | null>(null)
  const [error, setError] = useState<string | null>(null)
  const idempotencyRootRef = useRef<string | null>(null)

  const previewMutation = useMutation({
    mutationFn: async (input: ConvertToSalesOwnedInput) => {
      const response = await previewBillingOp({
        cost_center_id: costCenterId,
        operation_kind: OPERATION_KINDS.CONVERT_TO_SALES_OWNED,
        input: input as unknown as object,
      })
      return {
        preview: response.data.preview as unknown as BillingOpsPreviewView,
        input,
      }
    },
    onSuccess: ({ preview: result, input }) => {
      setPreview(result)
      setPreviewedInput(input)
      setError(null)
      idempotencyRootRef.current ??= crypto.randomUUID()
    },
    onError: (err) => {
      setPreview(null)
      setPreviewedInput(null)
      setError(isApiError(err) ? err.detail || err.message : 'Preview failed')
    },
  })

  const commitMutation = useMutation({
    mutationFn: async () => {
      if (!previewedInput || !idempotencyRootRef.current) throw new Error('missing commit context')
      await commitBillingOp({
        cost_center_id: costCenterId,
        operation_kind: OPERATION_KINDS.CONVERT_TO_SALES_OWNED,
        input: previewedInput as unknown as object,
        idempotency_root: idempotencyRootRef.current,
      })
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: enterpriseDetailsQueryKey(enterpriseId),
        }),
        queryClient.invalidateQueries({ queryKey: ['enterprise-pilots'] }),
      ]),
    onError: (err) => setError(isApiError(err) ? err.detail || err.message : 'Commit failed'),
  })

  const isPending = previewMutation.isPending || commitMutation.isPending
  const committed = commitMutation.isSuccess

  const handleFormChange = () => {
    setPreview(null)
    setPreviewedInput(null)
    setError(null)
    idempotencyRootRef.current = null
  }

  return (
    <DialogWrapper
      isOpen
      title={isSalesOwnedPilot ? 'Convert pilot' : 'Convert to sales-owned'}
      minWidth={640}
      close={commitMutation.isPending ? () => undefined : close}
    >
      <div className={styles.convertDialog}>
        <ConvertToSalesOwnedForm
          disabled={isPending || committed}
          onSubmit={(input) => previewMutation.mutate(input)}
          onChange={handleFormChange}
          formId={FORM_ID}
          banner={isSalesOwnedPilot ? PILOT_BANNER : undefined}
        />
        {preview && <PreviewPanel preview={preview} />}
        <p className={styles.convertHint}>
          {isSalesOwnedPilot
            ? committed
              ? 'Converted. The org now has sales-owned access with no end date.'
              : 'Only our database changes; this pilot has nothing in Stripe.'
            : committed
              ? 'Converted. Nothing was changed in Stripe: send Support the subscription and invoices listed above to cancel.'
              : 'Only our database changes; nothing is sent to Stripe. Support cancels the old Stripe subscription and invoices listed in the preview.'}
        </p>
        {error && <p className={styles.convertError}>{error}</p>}
        <div className={styles.headerActions}>
          {committed ? (
            <Button title="Done" type="dark" onClick={close} />
          ) : (
            <Button title="Cancel" type="light" onClick={close} isDisabled={commitMutation.isPending} />
          )}
          {committed ? null : preview ? (
            <Button
              title="Convert"
              type="destructive"
              onClick={() => commitMutation.mutate()}
              isLoading={commitMutation.isPending}
              isDisabled={previewMutation.isPending}
            />
          ) : (
            <button type="submit" form={FORM_ID} disabled={isPending} className={billingOpsStyles.opItem}>
              <span className={billingOpsStyles.opItemKind}>
                {previewMutation.isPending ? 'Previewing…' : 'Preview'}
              </span>
            </button>
          )}
        </div>
      </div>
    </DialogWrapper>
  )
}

export default ConvertToSalesOwnedDialog
